import { useCallback, useEffect, useMemo, useState } from "react";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { QuestionProps } from "./Props";
import { Button, Loading, Alert, Card, CardBody } from "@aidkitorg/component-library";
import { usePost } from "../API";
import { safeParse, useInterval } from "../Util";
import { useLocalizedStrings } from "../Localization";

interface GiveCardFormData extends Partial<{
  firstName: string;
  lastName: string;
  legalBusinessName: string;
  email: string;
}> {}

export function GiveCardIntake(props: QuestionProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<GiveCardFormData>({});
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const [bankTransferProgramId, setBankTransferProgramId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [showError, setShowError] = useState(false);
  const [intakeStatus, setIntakeStatus] = useState<string>('awaiting_recipient');
  const [isLoadingRecipient, setIsLoadingRecipient] = useState(false);
  const [isCompleteOnLoad, setIsCompleteOnLoad] = useState(false);

  const createGiveCardRecipient = usePost('/payments/create_givecard_recipient');
  const checkGiveCardIntake = usePost('/payments/check_givecard_intake');
  const getGiveCardBankTransferProgram = usePost('/payments/get_givecard_bank_transfer_program');

  const L = useLocalizedStrings();

  const givecardBaseURL = useMemo(() => {
    return window.location.hostname.includes('localhost') || window.location.hostname.includes('dev.aidkit.com')
      ? 'https://givecard-platform-dev.web.app' 
      : 'https://app.givecard.com';
  }, []);

  const type = useMemo(() => {
    const metadata = safeParse(props.Metadata || '{}');
    const rawType = metadata.recipientType || 'person';
    let typeToUse = rawType;
    if (rawType !== 'person' && rawType !== 'business') {
      const tfValue = props.info[rawType];
      if (tfValue === 'person' || tfValue === 'business') {
        typeToUse = tfValue;
      }
    }

    return typeToUse;
  }, [props.type, props.info]);

  const isFormValid = useMemo(() => {
    const { firstName, lastName, legalBusinessName, email } = formData;
    if (type !== 'person' && type !== 'business') {
      return false;
    }
    
    // Email is always required
    if (!email?.trim()) return false;
    
    if (type === 'person') {
      return !!(firstName?.trim() && lastName?.trim());
    } else {
      return !!legalBusinessName?.trim();
    }
  }, [formData, type]);

  // Auto-populate form data from applicant info
  useEffect(() => {
    const applicantInfo = props?.info || {};

    // A recipient has already been created for this applicant via this intake form. 
    // We do not want to create another.
    if (!!applicantInfo[props['Target Field'] + '_recipient_details']) {
      const recipientDetails = safeParse(applicantInfo[props['Target Field'] + '_recipient_details']);
      setFormData({
        firstName: recipientDetails.firstName,
        lastName: recipientDetails.lastName,
        legalBusinessName: recipientDetails.legalBusinessName,
        email: recipientDetails.email
      });

      setRecipientId(recipientDetails.recipientId);
      return;
    }
    
    // Split legal_name into first and last name
    const legalName = applicantInfo.legal_name || '';
    const nameParts = legalName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    setFormData({
      firstName: formData.firstName || firstName,
      lastName: formData.lastName || lastName,
      legalBusinessName: formData.legalBusinessName || applicantInfo.legal_business_name || '',
      email: formData.email || applicantInfo.email || ''
    });
  }, [props.info]);

  useAsyncEffect(async () => {
    const response = await getGiveCardBankTransferProgram({});
    if (response?.data) {
      setBankTransferProgramId(response.data);
    }
  }, []);

  useEffect(() => {
    if (!recipientId) return;
    
    // Create script element
    const script = document.createElement('script');
    script.src = 'https://www.app.givecard.com/plaid-host.js';
    script.async = true;
    document.head.appendChild(script);
    
    // Cleanup function to remove script when component unmounts
    return () => {
      document.head.removeChild(script);
    };
  }, [recipientId]);

  // Fetch recipient intake status
  const refreshGiveCardIntake = useCallback(async () => {
    if (!recipientId) return;
      
    setIsLoadingRecipient(true);
    let status = undefined;
    try {
      const intake = await checkGiveCardIntake({ recipientId });
      if ('intake_status' in intake) {
        status = intake.intake_status;
        // awaiting_recipient means the intake form has not been submitted - so we should not update the target field
        // since the question is not complete.
        if (status && status !== 'awaiting_recipient') {
          if (props['Target Field']) props.setInfoKey(props['Target Field'], status, true, false);
        }
        setIntakeStatus(status);
      } else {
        console.error('Intake status not found in response:', intake);
      }
    } catch (error) {
      console.error('Error fetching recipient status:', error);
    } finally {
      setIsLoadingRecipient(false);
    }
    return status;
  }, [recipientId]);

  // Check the status of the intake on load. If it's already complete, we will not show the iframe.
  useAsyncEffect(async () => {
    const status = await refreshGiveCardIntake();
    if (recipientId && (status === 'bank_account_verified' || status === 'pending_verification')) {
      setIsCompleteOnLoad(true);
    }
  }, [recipientId, refreshGiveCardIntake]);

  // When the iframe is open, we want to refresh the intake status every 3 seconds.
  useInterval(() => {
    if (!recipientId || isLoadingRecipient) return;
    refreshGiveCardIntake();
  }, 3000);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending_verification: { text: L.applicant.givecard_intake.pending_verification, className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      bank_account_verified: { text: L.applicant.givecard_intake.bank_account_verified, className: 'bg-green-100 text-green-800 border-green-200' },
      verification_failed: { text: L.applicant.givecard_intake.verification_failed, className: 'bg-red-100 text-red-800 border-red-200' }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;

    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${config.className}`}>
        {config.text}
      </div>
    );
  };

  const handleCreateAccount = useCallback(async () => {
    if (!isFormValid) return;
    
    setIsLoading(true);
    
    try {
      const requestBody = {
        email: formData.email!,
        programId: bankTransferProgramId!,
        applicant: props.uid,
        targetField: props['Target Field']!,
        type,
        ...(type === 'business' ? {
          legalBusinessName: formData.legalBusinessName!
        } : {
          firstName: formData.firstName!,
          lastName: formData.lastName!
        })
      };

      const response = await createGiveCardRecipient(requestBody);

      if (response?.data?.recipient) {
        props.setInfoKey(props['Target Field'] + '_recipient_details', JSON.stringify({
          recipientId: response.data?.recipient.id,
          firstName: response.data?.recipient.firstName,
          lastName: response.data?.recipient.lastName,
          legalBusinessName: response.data?.recipient.legalBusinessName,
          email: response?.data?.recipient.email,
        }), true, false);
        setShowError(false);
        setRecipientId(response.data?.recipient.id);
      } else {
        setShowError(true);
        if (typeof response?.error === 'string' && response?.error.includes('Message:')) {
          // GiveCard error messages are like this: Error code: 400 Message: recipient with given email already exists within the business.
          // Let's cut out the code to make it appear more professional for the applicant.
          setErrorMessage(response?.error.split('Message:')[1].trim());
        } else {  
          setErrorMessage(typeof response?.error === 'string' ? response?.error : response?.error?.message);
        }
        console.error('Failed to create GiveCard recipient:', response);
      }
    } catch (error) {
      setShowError(true);
      console.error('Error creating GiveCard recipient:', error);
    } finally {
      setIsLoading(false);
    }
  }, [bankTransferProgramId, createGiveCardRecipient, formData, props.setInfoKey, props.uid, props['Target Field'], type, isFormValid]);

  if (!bankTransferProgramId) {
    return <Loading />;
  } 

  return (
    <div>
      <div className="max-w-md mx-auto">
        <p className="mb-4">{L.applicant.givecard_intake.aidkit_partners_with_givecard}</p>
        {recipientId ? (
          <p className="mb-4">{L.applicant.givecard_intake.the_following_information_is_connected}</p>
        ) : (
          <p className="mb-4">{L.applicant.givecard_intake.the_following_information_will_be_connected}</p>
        )}
        
        <Card>
          <CardBody>
            <div className="mb-2">
              <div className="text-xs font-bold text-gray-400 ">{L.applicant.givecard_intake.email}</div>
              <div>{formData.email}</div>
            </div>

            {type === 'person' ? (
              <>
                <div className="mb-2">
                  <div className="text-xs font-bold text-gray-400 ">{L.applicant.givecard_intake.first_name}</div>
                  <div>{formData.firstName}</div>
                </div>

                <div className="">
                  <div className="text-xs font-bold text-gray-400">{L.applicant.givecard_intake.last_name}</div>
                  <div>{formData.lastName}</div>
                </div>
              </>
            ) : ( 
              <div className="">
                <div className="text-xs font-bold text-gray-400 ">{L.applicant.givecard_intake.legal_business_name}</div>
                <div>{formData.legalBusinessName}</div>
              </div>
            )}
          </CardBody>
        </Card>
        

        {showError && (
          <Alert
            variant="danger"
            className="mt-3"
            children={<div className="flex flex-col gap-2">
              <span className="font-bold">{L.applicant.givecard_intake.failed_to_create_account}</span>
              <span>{L.applicant.givecard_intake.error_details} {errorMessage || 'Unknown error'}</span>
            </div>}
          />
        )}

        {!recipientId && !isLoadingRecipient && (
          <Button
            onClick={handleCreateAccount}
            isDisabled={!isFormValid}
            loading={isLoading}
            className="mt-3"
          >
            Confirm Details
          </Button>
        )}
      </div>

      {recipientId && (
        <div className="mt-8 text-center">

          {!isCompleteOnLoad && <><iframe
            src={`${givecardBaseURL}/setup-payment/${bankTransferProgramId}/recipient`}
            sandbox="allow-scripts allow-same-origin allow-popups"
            scrolling="no"
            className={`w-full h-auto border-none ${(intakeStatus === 'pending_verification' || intakeStatus === 'bank_account_verified') ? 'min-h-[650px]' : 'min-h-[1050px]'}`}
            style={{
              overflow: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
            title="GiveCard Intake">
          </iframe>
          <script src="https://www.app.givecard.com/plaid-host.js"></script></>}

          {intakeStatus && (
            <div className="flex flex-col items-center gap-2">
              {getStatusBadge(intakeStatus)}
              {intakeStatus === 'pending_verification' && 
                L.applicant.givecard_intake.intake_form_submitted_pending_verification}
              {intakeStatus === 'bank_account_verified' && 
                L.applicant.givecard_intake.intake_form_submitted_bank_account_verified}
              {intakeStatus === 'verification_failed' && 
                L.applicant.givecard_intake.intake_form_submitted_verification_failed}
            </div>
          )}
        </div>
      )}
    </div>
  );
}